import React, {useEffect, useReducer, useState, useMemo, useCallback} from "react";
import {useActionConsumer} from "./useActionConsumer"
import {TabBody, TabHeader, Tabs} from "@ciq/shared/components/tabs";
import Indicator from "@ciq/shared/components/loader";
import Search from "@ciq/shared/components/search";
import {AddTextButton, Button, ConnectButton, ConnectingButton, DeleteButton, DisconnectButton, DisconnectingButton, PendingButton} from "@ciq/shared/components/buttons";
import {EntryInput, EntryModal, EntrySelector} from "@ciq/shared/components/modal";
import {POLL_INTERVAL} from "@ciq/shared/api/api";
import {EmptyView} from "@ciq/shared/components/views";
import {formatRelativeTime} from "@ciq/shared/utils/format";

const CONNECTED_TAB = {id: "connected", label: "Connected Apps"}
const UNCONNECTED_TAB = {id: "unconnected", label: "Unconnected Apps"}

const TABS = [
  CONNECTED_TAB,
  UNCONNECTED_TAB,
]

const DEFAULT_DATA = {
  connectionModal: {
    show: false,
    integration: null
  },
  disconnectModal: {
    show: false,
    integration: null
  },
  selectedTabId: CONNECTED_TAB.id,
}

const SHOW_CONNECTION_MODAL = "SHOW_CONNECTION_MODAL"
const SHOW_DISCONNECTION_MODAL = "SHOW_DISCONNECTION_MODAL"

const RESET = "RESET"
const SET_ACTIVE_TAB = "SET_ACTIVE_TAB"

const IntegrationReducer = (state = {}, action) => {
  switch (action.type) {
    case SHOW_CONNECTION_MODAL:
      return {
        ...state,
        connectionModal: {
          show: action.payload != null,
          integration: action.payload
        }
      }

    case SHOW_DISCONNECTION_MODAL:
      return {
        ...state,
        disconnectModal: {
          show: action.payload != null,
          integration: action.payload
        }
      }

    case RESET:
      return DEFAULT_DATA

    case SET_ACTIVE_TAB:
      return {
        ...state,
        selectedTabId: action.payload
      }

    default:
      throw new Error(`Unknown action type ${action.type}`);
  }
}

const areConnectionsProcessing = (integrations) => {
  const isUnConnectedProcessing = integrations && integrations.unconnected && integrations.unconnected.flatMap(u => u.integrations).filter(u => u.is_processing).length > 0
  const isConnectedProcessing = integrations && integrations.connected && integrations.connected.flatMap(u => u.integrations).filter(u => u.is_processing).length > 0
  return isUnConnectedProcessing || isConnectedProcessing
}

export function SchoolIntegrationPane({show, cancelPolling, integrations, onInflateIntegration, onConnect, onDisconnect, onPoll, onActionCall}) {
  const [state, dispatch] = useReducer(IntegrationReducer, DEFAULT_DATA)

  const showConnectionModal = integration => dispatch({type: SHOW_CONNECTION_MODAL, payload: integration})
  const hideConnectionModal = () => dispatch({type: SHOW_CONNECTION_MODAL, payload: null})

  const showDisconnectionModal = integration => dispatch({type: SHOW_DISCONNECTION_MODAL, payload: integration})
  const hideDisconnectionModal = () => dispatch({type: SHOW_DISCONNECTION_MODAL, payload: null})

  const resetData = () => dispatch({type: RESET})
  const setSelectedTab = tab => dispatch({type: SET_ACTIVE_TAB, payload: tab.id})

  const isProcessing = areConnectionsProcessing(integrations)

  useEffect(() => {
    resetData()

    let interval = null
    let controller = null

    if (show && !cancelPolling && isProcessing) {
      interval = setInterval(() => {
        controller = new AbortController()
        onPoll(controller.signal)
      }, POLL_INTERVAL)
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }

      if (controller) {
        controller.abort()
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, cancelPolling, isProcessing]);

  if (!show) return null

  const notifications = integrations.notifications
  const tabs = TABS.map(tab => ({
    ...tab,
    badgeCount: notifications[tab.id]
  }))

  return (
    <>
      <Tabs>
        <TabHeader selectedTab={state.selectedTabId} items={tabs} onSelectTab={setSelectedTab}/>
        <TabBody className="settings-integrations-tab-body">
          <ConnectedApp
            show={state.selectedTabId === CONNECTED_TAB.id}
            integrations={integrations.connected}
            onDisconnect={integration => {
              showDisconnectionModal(integration)
            }}
          />
          <UnConnectedApp
            show={state.selectedTabId === UNCONNECTED_TAB.id}
            integrations={integrations.unconnected}
            onConnect={(integration) => {
              if (integration.has_next_step) {
                showConnectionModal(integration)
                return
              }

              onConnect && onConnect(integration, {})
            }}
          />
        </TabBody>
      </Tabs>
      <Indicator show={state.loading}/>
      <ConnectionModal
        onInflate={onInflateIntegration}
        onConnect={(integration, data) => {
          onConnect && onConnect(integration, data, hideConnectionModal)
        }}
        onHide={hideConnectionModal}
        onActionCall={onActionCall}
        {...state.connectionModal}
      />
      <DisconnectionModal
        onDisconnect={integration => {
          onDisconnect && onDisconnect(integration, hideDisconnectionModal)
        }}
        onHide={hideDisconnectionModal}
        {...state.disconnectModal}
      />
    </>
  )
}

function ConnectedApp({show, integrations, onDisconnect}) {
  return (
    <IntegrationContent
      show={show}
      integrations={integrations}
      emptyText="No Connected Apps"
      actionLoader={(integration) => {
        if (integration.is_processing) {
          return <DisconnectingButton className="settings-integration-action-button-primary" disabled/>
        }

        return <DisconnectButton className="settings-integration-action-button-danger" onClick={() => onDisconnect && onDisconnect(integration)}/>
      }}
    />
  )
}

function UnConnectedApp({show, integrations, onConnect}) {
  return (
    <IntegrationContent
      show={show}
      integrations={integrations}
      emptyText="No Unconnected Apps"
      actionLoader={(integration) => {
        if (integration.is_processing) {
          return <ConnectingButton className="settings-integration-action-button-primary" disabled/>
        }

        if (integration.state === 2) {
          return <PendingButton className="settings-integration-action-button-primary" disabled/>
        }

        return <ConnectButton className="settings-integration-action-button-primary" data-integration-name={integration.name} onClick={() => onConnect && onConnect(integration)}/>
      }}
    />
  )
}

const DEFAULT_INTEGRATION_DATA = {
  isSearching: false,
  search: "",
  lowered: null
}

const SET_SEARCH = "SET_SEARCH"
const setSearchEvent = search => ({type: SET_SEARCH, payload: search})

const IntegrationContentReducer = (state = {}, action) => {
  switch (action.type) {
    case SET_SEARCH:
      return {
        ...state,
        isSearching: action.payload != null,
        search: action.payload || "",
        lowered: action.payload != null ? action.payload.toLowerCase() : null,
      }

    default:
      throw new Error(`Unknown action type ${action.type}`);
  }
}

function IntegrationContent({show, integrations, emptyText, actionLoader}) {
  const [state, dispatch] = useReducer(IntegrationContentReducer, DEFAULT_INTEGRATION_DATA)

  const setSearch = search => dispatch(setSearchEvent(search))

  useEffect(() => {
    setSearch(null)
  }, [show]);

  if (!show) return null

  const hasIntegrations = !!integrations && integrations.length > 0
  const searchHandler = integration => {
    if (state.lowered == null) return true
    return integration.name.toLowerCase().includes(state.lowered)
      || (integration.description && integration.description.toLowerCase().includes(state.lowered))
  }

  const filteredGroups = hasIntegrations
    ? integrations.map(group => ({
        ...group,
        integrations: group.integrations.filter(searchHandler),
      })).filter(group => group.integrations.length > 0)
    : []

  return (
    <>
      <Search search={state.search} onSearch={setSearch}/>
      {filteredGroups.length === 0 && <EmptyView>{state.isSearching ? "No apps match your search." : emptyText}</EmptyView>}
      {filteredGroups.map(group => (
        <IntegrationGroup key={group.name} name={group.name}>
          {group.integrations.map(integration => (
            <Integration key={integration.name} integration={integration}>
              {actionLoader(integration)}
            </Integration>
          ))}
        </IntegrationGroup>
      ))}
    </>
  )
}

function IntegrationGroup({name, ...props}) {
  return (
    <div className="settings-integration-group">
      <span className="settings-integration-group-name">{name}</span>
      <div className="settings-integration-group-items">
        {props.children}
      </div>
    </div>
  )
}

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000

/**
 * @typedef {Object} LastDataReceivedResult
 * @property {string} text - Display text for the timestamp
 * @property {boolean} isStale - Boolean indicating if the data is stale (>24h)
 */

/**
 * Formats a last_data_received epoch timestamp into display text with staleness indicator.
 *
 * @param {number|null} value - Epoch timestamp in seconds, or null
 * @returns {LastDataReceivedResult|null} The formatted display text and staleness flag, or null if no data
 */
export function formatLastDataReceived(value) {
  if (value == null) {
    return null
  }

  const relativeText = formatRelativeTime(value)
  const tsMs = value * 1000
  const isStale = (Date.now() - tsMs) >= STALE_THRESHOLD_MS

  return {
    text: `Last data received: ${relativeText}`,
    isStale,
  }
}

function Integration({integration, ...props}) {
  const lastDataInfo = formatLastDataReceived(integration.last_data_received)

  return (
    <div className="settings-integration-group-item">
      <div className="settings-integration-group-item-info">
        <span className="settings-integration-group-item-name">{integration.name}</span>
        <span className="settings-integration-group-item-description">{integration.description}</span>
        {lastDataInfo && (
          <span className={`settings-integration-group-item-timestamp${lastDataInfo.isStale ? " settings-integration-group-item-timestamp--stale" : ""}`}>
            {lastDataInfo.isStale && "\u26A0 "}{lastDataInfo.text}
          </span>
        )}
      </div>
      <div className="settings-integration-group-item-actions">
        {props.children}
      </div>
    </div>
  )
}

function ConnectionModal({show, integration, onInflate, onConnect, onHide, onActionCall}) {
  const [data, setData] = useState({})
  const [values, setValues] = useState({})
  const [error, setError] = useState(null)
  const hasError = !!error

  const integrationId = integration ? integration.id : null

  // Bind integration ID into action calls so the hook doesn't need to know it
  const boundActionCall = useCallback((actionPath, payload, signal) => {
    if (!onActionCall || !integrationId) return Promise.reject({error: new Error("No integration"), handled: true})
    return onActionCall(integrationId, actionPath, payload, signal)
  }, [onActionCall, integrationId])

  const {actionFields, actionLoading, actionErrors, clearDependents, captureLabelValues, reset: resetActions, verificationState, verifyError, verify} = useActionConsumer(data.actions, values, boundActionCall)

  useEffect(() => {
    setData({})
    setValues({})
    resetActions()
  }, [show, resetActions]);

  useEffect(() => {
    if (!integrationId || onInflate == null) {
      return
    }

    let cancelled = false

    onInflate(integrationId, (data, error) => {
      if (!cancelled) {
        if (data != null) {
          setData(data)
        } else if (error != null) {
          setError(error)
        }
      }
    })

    return () => { cancelled = true }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId]);

  // Merge static write fields with action-populated fields for validation
  const allWriteFields = useMemo(() => {
    return {...(data.write || {}), ...actionFields}
  }, [data.write, actionFields])

  const isButtonDisabled = () => {
    const hasWrites = Object.keys(allWriteFields).length > 0
    const hasValues = Object.keys(values).length > 0

    if (hasError || !hasWrites || !hasValues) return true

    // When actions exist, require verification before connecting
    if (data.actions && data.actions.length > 0 && verificationState !== "verified") {
      return true
    }

    // Check if any required action-populated fields haven't loaded yet
    if (data.actions) {
      for (const action of data.actions) {
        for (const [fieldKey, fieldConfig] of Object.entries(action.populates || {})) {
          if (fieldConfig.required !== false && !(fieldKey in actionFields)) {
            // Required action field not yet populated — disable button
            return true
          }
        }
      }
    }

    // Check if any action is currently loading
    if (Object.values(actionLoading).some(v => v)) return true

    const entries = Object.entries(allWriteFields)
    const requiredEntries = entries.filter(([, entry]) => entry.required !== false)
    const objects = requiredEntries.map(([key, entry]) => {
      if (values[key] == null) {
        return false
      }

      if (entry.type === "string") {
        return values[key].length > 0
      }

      if (entry.type === "array") {
        const arrayValues = values[key]
        if (!arrayValues.length) {
          return false
        }

        return arrayValues.filter(v => v != null && v.length > 0)
          .length > 0
      }

      if (entry.type === "select") {
        return values[key] != null
      }

      if (entry.type === "multi-select") {
        return Array.isArray(values[key]) && values[key].length > 0
      }

      throw new Error(`Unsupported type ${entry.type}`)
    })

    return objects.filter(v => v).length !== requiredEntries.length
  }

  const handleOnClick = () => {
    if (onConnect == null) {
      return
    }

    const cleanValues = {}
    for (const [key, value] of Object.entries(values)) {
      if (Array.isArray(value)) {
        // multi-select: extract .value from option objects; array: filter empties
        cleanValues[key] = value
          .filter(v => v !== null)
          .map(v => (v && typeof v === "object" && "value" in v) ? v.value : v)
          .filter(v => v != null && (typeof v !== "string" || v.length > 0))
      } else if (value && typeof value === "object" && "value" in value) {
        // select: extract .value from option object
        cleanValues[key] = value.value
      } else {
        cleanValues[key] = value
      }
    }

    // Spread captured label values (e.g., org_name, network_name) into payload
    const capturedLabels = captureLabelValues(values)
    Object.assign(cleanValues, capturedLabels)

    onConnect(integration, cleanValues)
  }

  const handleOnChange = (key, value) => {
    const dependentClears = clearDependents(key)
    setValues(prev => ({
      ...prev,
      ...dependentClears,
      [key]: value,
    }))
  }

  // Reset handler: clears verification state AND gated/action-populated values
  // to prevent stale cross-org data from persisting after re-verify
  const handleReset = useCallback(() => {
    resetActions()
    const gatedKeys = new Set(
      Object.entries(data.write || {})
        .filter(([, entry]) => entry.dependsOn === "verify")
        .map(([key]) => key)
    )
    const actionPopulatedKeys = new Set()
    for (const action of (data.actions || [])) {
      for (const [key, config] of Object.entries(action.populates || {})) {
        actionPopulatedKeys.add(key)
        if (config.capture_label_as) actionPopulatedKeys.add(config.capture_label_as)
      }
    }
    setValues(prev => {
      const next = {}
      for (const [k, v] of Object.entries(prev)) {
        if (!gatedKeys.has(k) && !actionPopulatedKeys.has(k)) next[k] = v
      }
      return next
    })
  }, [resetActions, data])

  const buttons = {
    secondary: {
      title: "Cancel",
      onClick: onHide,
    },
    primary: {
      title: "Connect",
      disabled: isButtonDisabled(),
      onClick: handleOnClick
    }
  }

  return (
    <EntryModal cardClassName="settings-integration-connect-modal-card" show={show} title="Connect App" onHide={onHide} buttons={buttons}>
      {hasError && <EmptyView>{error}</EmptyView>}
      {!hasError && <ConnectionBody
        data={data}
        values={values}
        actionFields={actionFields}
        actionLoading={actionLoading}
        actionErrors={actionErrors}
        verificationState={verificationState}
        verifyError={verifyError}
        verify={verify}
        onReset={handleReset}
        onChange={handleOnChange}
      />}
    </EntryModal>
  )
}

function ConnectionBody({data, values, actionFields, actionLoading, actionErrors, verificationState, verifyError, verify, onReset, onChange}) {
  const hasActions = data.actions && data.actions.length > 0

  // Find which action is loading or errored for a given populated field
  const getActionStateForField = (fieldKey) => {
    if (!data.actions) return {loading: false, error: null}
    for (const action of data.actions) {
      if (action.populates && fieldKey in action.populates) {
        return {
          loading: !!actionLoading[action.name],
          error: actionErrors[action.name] || null,
        }
      }
    }
    return {loading: false, error: null}
  }

  /**
   * Render a single form field with optional editability/disabled overrides.
   *
   * @param {string} key - Field key
   * @param {Object} entry - Field config
   * @param {number} index - Render index (for autoFocus)
   * @param {Object} [opts] - Optional overrides
   * @param {boolean} [opts.editable] - Override editable state for string fields
   * @param {boolean} [opts.disabled] - Override disabled state for select fields
   */
  const renderField = (key, entry, index, opts = {}) => {
    if (entry.type === "string") {
      return (
        <EntryInput key={`connection-entry-${key}`} autoFocus={index === 0} label={entry.label} value={values[key] || ""} type={entry.sensitive ? "password" : "text"}
          disabled={opts.disabled || false}
          onChange={(value) => {
            onChange(key, value)
          }}/>
      )
    }

    if (entry.type === "array") {
      return <ArrayElement key={`connection-entry-${key}`} entry={entry} values={values[key] || []} onChange={(value) => {
        onChange(key, value)
      }}/>
    }

    if (entry.type === "select") {
      const actionState = getActionStateForField(key)
      const isDisabled = opts.disabled !== undefined ? opts.disabled : actionState.loading
      return (
        <div key={`connection-entry-${key}`}>
          <EntrySelector
            label={entry.label}
            placeholder={actionState.loading ? "Loading..." : undefined}
            options={entry.options || []}
            value={values[key] || null}
            disabled={isDisabled}
            onChange={(value) => onChange(key, value)}
          />
          {actionState.error && <span className="settings-integration-action-error">{actionState.error}</span>}
        </div>
      )
    }

    if (entry.type === "multi-select") {
      const actionState = getActionStateForField(key)
      const isDisabled = opts.disabled !== undefined ? opts.disabled : actionState.loading
      return (
        <div key={`connection-entry-${key}`}>
          <EntrySelector
            label={entry.label}
            placeholder={actionState.loading ? "Loading..." : undefined}
            options={entry.options || []}
            value={values[key] || []}
            isMulti={true}
            disabled={isDisabled}
            onChange={(value) => onChange(key, value)}
          />
          {actionState.error && <span className="settings-integration-action-error">{actionState.error}</span>}
        </div>
      )
    }

    throw new Error(`Unsupported entry type: ${entry.type}`)
  }

  // Compute field partitions when actions exist
  // triggerFieldKeys: write fields that appear in any action's trigger_fields
  // Only includes keys that are actual write fields (not action-populated fields like org_id)
  const triggerFieldKeys = useMemo(() => {
    if (!hasActions) return new Set()
    const writeKeys = new Set(Object.keys(data.write || {}))
    const keys = new Set()
    for (const action of data.actions) {
      for (const tf of action.trigger_fields || []) {
        if (writeKeys.has(tf)) {
          keys.add(tf)
        }
      }
    }
    return keys
  }, [hasActions, data.actions, data.write])

  // Check if all trigger write fields are filled (for verify button enablement)
  const allTriggersFilled = useMemo(() => {
    if (!hasActions) return false
    for (const key of triggerFieldKeys) {
      const v = values[key]
      if (v == null || v === "") return false
    }
    return triggerFieldKeys.size > 0
  }, [hasActions, triggerFieldKeys, values])

  // No-action integrations: render exactly as before
  if (!hasActions) {
    return (
      <div className="settings-integration-connect-modal-entry-container">
        <div>
          {Object.entries(data.read || {}).map(([key, entry], i) =>
            <EntryInput key={`connection-entry-${i}`} label={entry.label} value={entry.value} editable={false} copyable={true}/>
          )}
        </div>
        <div>
          {Object.entries(data.write || {}).map(([key, entry], i) => renderField(key, entry, i))}
          {Object.entries(actionFields || {}).map(([key, entry], i) => renderField(key, entry, Object.keys(data.write || {}).length + i))}
        </div>
      </div>
    )
  }

  // Partition write fields into trigger, gated, and ungated
  const writeEntries = Object.entries(data.write || {})
  const triggerFields = writeEntries.filter(([key]) => triggerFieldKeys.has(key))
  const gatedFields = writeEntries.filter(([, entry]) => entry.dependsOn === "verify")
  const ungatedFields = writeEntries.filter(([key, entry]) => !triggerFieldKeys.has(key) && entry.dependsOn !== "verify")

  const triggerDisabled = verificationState === "verified" || verificationState === "verifying"
  const isVerified = verificationState === "verified"

  return (
    <div className="settings-integration-connect-modal-entry-container">
      <div>
        {Object.entries(data.read || {}).map(([key, entry], i) =>
          <EntryInput key={`connection-entry-${i}`} label={entry.label} value={entry.value} editable={false} copyable={true}/>
        )}
      </div>
      <div>
        {/* Trigger fields — editable only when idle or error */}
        {triggerFields.map(([key, entry], i) => renderField(key, entry, i, {disabled: triggerDisabled}))}

        {/* Ungated non-trigger fields — always editable */}
        {ungatedFields.map(([key, entry], i) => renderField(key, entry, triggerFields.length + i))}

        {/* Verify gate section */}
        <div className="settings-integration-verify-gate">
          <Button
            variant="primary"
            className="settings-integration-action-button-primary"
            disabled={verificationState === "verifying" || (verificationState === "idle" && !allTriggersFilled)}
            onClick={verificationState === "verified" ? onReset : verify}
          >
            {verificationState === "verified" ? "Reset" : verificationState === "error" ? "Retry" : verificationState === "verifying" ? <span className="settings-integration-verify-spinner spinner-border spinner-border-sm" role="status" aria-label="Verifying" /> : "Verify"}
          </Button>
          {verifyError && <span className="settings-integration-action-error">{verifyError}</span>}
        </div>

        {/* Action-populated fields — always visible, disabled until verified */}
        {(() => {
          // Build field entries from action populates config, merged with actual options from actionFields
          const populatedEntries = {}
          for (const action of data.actions) {
            for (const [fieldKey, fieldConfig] of Object.entries(action.populates || {})) {
              populatedEntries[fieldKey] = actionFields[fieldKey] || {...fieldConfig, options: []}
            }
          }
          return Object.entries(populatedEntries).map(([key, entry], i) =>
            renderField(key, entry, triggerFields.length + ungatedFields.length + i, {disabled: !isVerified})
          )
        })()}

        {/* Gated fields — disabled when not verified */}
        {gatedFields.map(([key, entry], i) =>
          renderField(key, entry, triggerFields.length + ungatedFields.length + Object.keys(actionFields || {}).length + i, {disabled: !isVerified})
        )}
      </div>
    </div>
  )
}

function ArrayElement({entry, values: originalValues, onChange}) {
  const values = [...originalValues]

  if (!values.length) {
    values.push("")
  }

  return (
    <div className="settings-integration-connect-modal-entry-array-container da-modal-entry-input">
      <span>{entry.label}</span>

      {values.map((value, i) =>
        <div key={`setting-array-option-${i}`}>
          <input value={value} onChange={e => {
            const newValue = e.target.value
            onChange(values.map((v, j) => i === j ? newValue : v))
          }}/>
          <DeleteButton onClick={() => {
            if (values.length <= 1) {
              return
            }

            onChange(values.filter((v, j) => i !== j))
          }}/>
        </div>
      )}
      <AddTextButton onClick={() => onChange([...values, ""])}>{`Add ${entry.item_name}`}</AddTextButton>
    </div>
  )
}

function DisconnectionModal({show, integration, onDisconnect, onHide}) {
  const [current, setCurrent] = useState("")

  useEffect(() => {
    setCurrent("")
  }, [show]);

  if (!show || integration == null) return null

  const handleOnClick = () => onDisconnect && onDisconnect(integration)

  const buttons = {
    secondary: {
      title: "No, keep this app",
      onClick: onHide,
    },
    danger: {
      title: "Yes, disconnect this app",
      disabled: current !== integration.name,
      onClick: handleOnClick
    }
  }

  return (
    <EntryModal cardClassName="da-modal-delete-content" bodyClassName="da-modal-delete-body" show={show} title="Connect App" onHide={onHide} buttons={buttons}>
      <p>{`This action can not be undone. The disconnection of ${integration.name} is permanent and will effect all users of the app`}</p>
      <div>
        <p>Please type <span>{integration.name}</span> to confirm</p>
        <input autoFocus value={current} onChange={(e) => setCurrent(e.target.value)}/>
      </div>
    </EntryModal>
  )
}

// Export reducer, utility functions, and constants for testing
export {
  IntegrationReducer,
  areConnectionsProcessing,
  DEFAULT_DATA,
  SHOW_CONNECTION_MODAL,
  SHOW_DISCONNECTION_MODAL,
  RESET,
  SET_ACTIVE_TAB,
  CONNECTED_TAB,
  UNCONNECTED_TAB,
}
